import { FlakinessReport } from '@flakiness/flakiness-report';
import { ReportUtils } from '@flakiness/sdk/browser';
import { WireTypes } from '../wireTypes.js';
import { Stats as S } from './stats.js';
import { parseTestsAndEnvs, serializeTestsAndEnvs, TestIndex, TestLookup } from './testIndex.js';

/**
 * Collects the tests and environments of reports. They are only ever appended,
 * so an index that was handed out stays valid. `build()` gives a `TestIndex` to
 * read them with.
 */
export class TestIndexBuilder implements TestLookup {
  private _tests: WireTypes.Test[];
  private _envs: WireTypes.RunEnvironment[];
  private _envIdToIndex: Map<S.EnvId, S.EnvIndex>;
  private _testIdToIndex: Map<S.TestId, S.TestIndex>;
  private _epoch: S.TestsEpoch;

  constructor(json: S.JSONTestsAndEnvs = S.createEmptyTests()) {
    const { epoch, tests, envs, testIdToIndex, envIdToIndex } = parseTestsAndEnvs(json);
    this._epoch = epoch;
    this._tests = tests;
    this._envs = envs;
    this._testIdToIndex = testIdToIndex;
    this._envIdToIndex = envIdToIndex;
  }

  epoch() {
    return this._epoch;
  }

  testIndex(testId: S.TestId) {
    return this._testIdToIndex.get(testId);
  }

  envIndex(envId: S.EnvId) {
    return this._envIdToIndex.get(envId);
  }

  countTests() {
    return this._tests.length;
  }

  // Since index might be mutated later down while adding this report, and server might add new tests
  // as well, we need to make sure that all methods that return "expanded" test outcomes from server - i.e. those
  // containing actual test ranges - are pre-cut to include tests only until this index.
  // All these methods have required `cutoffTestIndex` parameter for this purpose.
  cutoffIndex() {
    return (this._tests.length - 1) as S.TestIndex;
  }

  addReport(report: FlakinessReport.Report): S.EnvIndex[] {
    // Add missing tests.
    ReportUtils.visitTests(report, (test, parentSuites) => {
      const wireTest = S.flakinessTestToWireTypesTest(test, parentSuites);
      let testIdx = this._testIdToIndex.get(wireTest.testId);
      if (testIdx === undefined) {
        testIdx = this._tests.push(wireTest) - 1 as S.TestIndex;
        this._testIdToIndex.set(wireTest.testId, testIdx);
      }
    });
    const result: S.EnvIndex[] = [];
    for (const fkEnv of report.environments) {
      const env = S.jsonEnvToWireEnv(S.flakinessEnvToJSONEnv(report, fkEnv));
      let envIdx = this._envIdToIndex.get(env.envId);
      if (envIdx === undefined) {
        envIdx = this._envs.push(env) - 1 as S.EnvIndex;
        this._envIdToIndex.set(env.envId, envIdx);
      }
      result.push(envIdx);
    }
    return result;
  }

  serialize(): S.JSONTestsAndEnvs {
    return serializeTestsAndEnvs({ epoch: this._epoch, tests: this._tests, envs: this._envs });
  }

  /**
   * An index of everything added so far. The lists and the maps are copied, so
   * that the reports added afterwards do not show up in it. The tests and the
   * environments themselves are shared: they never change once they are added.
   */
  build(): TestIndex {
    return new TestIndex({
      epoch: this._epoch,
      tests: this._tests.slice(),
      envs: this._envs.slice(),
      testIdToIndex: new Map(this._testIdToIndex),
      envIdToIndex: new Map(this._envIdToIndex),
    });
  }
}
